import { captureException } from "@sentry/browser";
import {
  fetchSignInMethodsForEmail,
  GithubAuthProvider,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  OAuthProvider,
  SAMLAuthProvider,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
} from "firebase/auth";
import React, { useState } from "react";

import { getAuth } from "@/auth/firebase";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import "./CustomAuthUI.css";

const auth = getAuth();

interface CustomAuthUIProps {
  signInOptions: Array<string | { provider: string; providerName?: string }>;
  onSignInSuccess?: () => void;
  emailLinkConfig?: {
    url: string;
    handleCodeInApp: boolean;
  };
}

const invalidSignInLinkMessage = `
  Sign-in link invalid. This can happen if the code is malformed, expired, or
  has already been used. It can also happen if you use the sign-in link on a
  different device or browser than the one where you started the sign-in
  process. Try requesting a new sign-in link, and opening the link using the
  same device and browser.`;

type EmailLinkState = "none" | "input" | "sent" | "troubleshooting";

export const CustomAuthUI: React.FC<CustomAuthUIProps> = ({
  signInOptions,
  onSignInSuccess,
  emailLinkConfig,
}) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailLinkState, setEmailLinkState] = useState<EmailLinkState>("none");

  React.useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const savedEmail = window.localStorage.getItem("emailForSignIn");
      if (savedEmail) {
        handleEmailLinkCompletion(savedEmail);
      } else {
        setError(invalidSignInLinkMessage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleError = (err: any, fallbackMessage?: string) => {
    if (
      err.code === "auth/invalid-action-code" ||
      err.code === "auth/invalid-email"
    ) {
      setError(invalidSignInLinkMessage);
    } else if (err.code === "auth/popup-blocked") {
      setError(
        `Authentication popup window was blocked. Please allow pop-ups and try again.`,
      );
    } else if (err.code === "auth/account-exists-with-different-credential") {
      const email = err.customData?.email;
      if (!email) {
        setError(
          "An account already exists with the same email address. Please sign in with the same method you originally used. Contact support@freshpaint.io for assistance.",
        );
      }

      setLoading(true);
      checkEmailLinkAccountAlreadyExists(email)
        .then((errorMessage) => {
          if (!errorMessage) {
            setError(
              "An account already exists with the same email address. Please sign in with the same method you originally used. Contact support@freshpaint.io for assistance.",
            );
          }

          setError(errorMessage);
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (
      err.code === "auth/popup-closed-by-user" ||
      err.code === "auth/cancelled-popup-request" ||
      err.code === "auth/user-cancelled"
    ) {
      // Ignore - user will just try again
    } else {
      const message = fallbackMessage || "An unexpected error occurred";
      setError(
        `${message}. Please try again or contact support@freshpaint.io.`,
      );
      console.log(message, err);
      captureException(err);
    }
  };

  const handleEmailLinkCompletion = async (emailAddress: string) => {
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailLink(auth, emailAddress, window.location.href);
      window.localStorage.removeItem("emailForSignIn");
      onSignInSuccess?.();
    } catch (err: any) {
      handleError(err, "Failed to complete sign-in with email link");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      onSignInSuccess?.();
    } catch (err: any) {
      handleError(err, "Failed to sign in with Google");
    }
  };

  const handleGithubSignIn = async () => {
    setError(null);
    try {
      const provider = new GithubAuthProvider();
      await signInWithPopup(auth, provider);
      onSignInSuccess?.();
    } catch (err: any) {
      handleError(err, "Failed to sign in with GitHub");
    }
  };

  const handleSAMLSignIn = async (providerId: string, providerName: string) => {
    setError(null);
    try {
      const provider = new SAMLAuthProvider(providerId);
      await signInWithPopup(auth, provider);
      onSignInSuccess?.();
    } catch (err: any) {
      handleError(err, `Failed to sign in with ${providerName}`);
    }
  };

  const handleOIDCSignIn = async (providerId: string, providerName: string) => {
    setError(null);
    try {
      const provider = new OAuthProvider(providerId);
      await signInWithPopup(auth, provider);
      onSignInSuccess?.();
    } catch (err: any) {
      handleError(err, `Failed to sign in with ${providerName}`);
    }
  };

  const sendEmailLink = async (emailAddress: string) => {
    if (!emailLinkConfig) throw new Error("Email link config is not set");
    await sendSignInLinkToEmail(auth, emailAddress, emailLinkConfig);
    window.localStorage.setItem("emailForSignIn", emailAddress);
  };

  const checkEmailLinkAccountAlreadyExists = async (
    emailAddress: string,
  ): Promise<string | null> => {
    const signInMethods = await fetchSignInMethodsForEmail(auth, emailAddress);

    if (signInMethods.length > 0 && !signInMethods.includes("emailLink")) {
      const providerNames = signInMethods.map((method) => {
        if (method === "google.com") return "Google";
        if (method === "github.com") return "GitHub";
        if (method.startsWith("saml.")) return "SSO";
        if (method.startsWith("oidc.")) return "SSO";
        return method;
      });

      return `This account already exists. Please sign in using ${providerNames.join(", ")}.`;
    }

    return null;
  };

  const handleEmailLinkRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !emailLinkConfig) return;

    setLoading(true);
    setError(null);
    try {
      const errorMessage = await checkEmailLinkAccountAlreadyExists(email);
      if (errorMessage) {
        setEmailLinkState("none");
        setError(errorMessage);
        return;
      }

      await sendEmailLink(email);
      setEmailLinkState("sent");
    } catch (err: any) {
      setEmailLinkState("none");
      handleError(err, "Failed to send sign-in link");
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmailLink = async () => {
    if (!email || !emailLinkConfig) return;
    setLoading(true);
    setError(null);
    try {
      await sendEmailLink(email);
      setEmailLinkState("sent");
    } catch (err: any) {
      setEmailLinkState("none");
      handleError(err, "Failed to resend sign-in link");
    } finally {
      setLoading(false);
    }
  };

  const renderProviderButton = (option: any) => {
    if (typeof option === "string") {
      if (option === "google.com") {
        return (
          <div key="google" className="custom-auth-ui-item">
            <button
              type="button"
              className="text-textSubtle bg-surface-default flex w-full cursor-pointer items-center justify-center rounded-sm border-none px-4 py-2 text-sm font-medium"
              onClick={handleGoogleSignIn}
            >
              <svg
                className="mr-4 h-[18px] w-[18px] shrink-0"
                viewBox="0 0 24 24"
              >
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </button>
          </div>
        );
      } else if (option === "github.com") {
        return (
          <div key="github" className="custom-auth-ui-item">
            <button
              type="button"
              className="flex w-full cursor-pointer items-center justify-center rounded-sm border-none bg-[#333333] px-4 py-2 text-sm font-medium text-white"
              onClick={handleGithubSignIn}
            >
              <img
                className="mr-4 h-[18px] w-[18px] shrink-0"
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/github.svg"
              />
              Sign in with GitHub
            </button>
          </div>
        );
      }
    } else if (
      typeof option === "object" &&
      option.provider === "password" &&
      option.signInMethod === "emailLink"
    ) {
      return (
        <div key="email-link" className="custom-auth-ui-item">
          <button
            type="button"
            className="flex w-full cursor-pointer items-center justify-center rounded-sm border-none bg-[#db4437] px-4 py-2 text-sm font-medium text-white"
            onClick={() => {
              setEmailLinkState("input");
            }}
          >
            <img
              className="mr-4 h-[18px] w-[18px] shrink-0"
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/mail.svg"
            />
            Sign in with Email
          </button>
        </div>
      );
    } else if (typeof option === "object" && option.provider) {
      const providerId = option.provider;
      const providerName = option.providerName || providerId;
      const isSAML = providerId.startsWith("saml.");

      return (
        <div key={providerId} className="custom-auth-ui-item">
          <button
            type="button"
            className="flex w-full cursor-pointer items-center justify-center rounded-sm border-none bg-[#007bff] px-4 py-2 text-left text-sm font-medium text-white"
            onClick={() =>
              isSAML
                ? handleSAMLSignIn(providerId, providerName)
                : handleOIDCSignIn(providerId, providerName)
            }
          >
            <img
              className="mr-4 h-[18px] w-[18px] shrink-0"
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/saml.svg"
            />
            Sign in with {providerName}
          </button>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (emailLinkState !== "none") {
    return (
      <>
        {emailLinkState === "input" ? (
          <form
            onSubmit={handleEmailLinkRequest}
            className="flex flex-col items-center"
          >
            <label htmlFor="ui-sign-in-email-input" className="sr-only">
              Email
            </label>
            <input
              id="ui-sign-in-email-input"
              name="email"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className="border-borderDefault focus:border-interactive-selected mb-4 w-full rounded border px-3 py-2 text-base focus:outline-none"
              autoComplete="username"
            />
            <div className="flex w-full flex-row items-center justify-end gap-5">
              <button
                type="button"
                className="bg-fill-neutral text-textDefault cursor-pointer rounded px-4 py-2 text-sm font-medium hover:bg-gray-300"
                onClick={() => setEmailLinkState("none")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="cursor-pointer rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Send sign-in link
              </button>
            </div>
          </form>
        ) : (
          <div key="email-link" className="custom-auth-ui-item text-left">
            {emailLinkState === "troubleshooting" ? (
              <>
                <h1>Trouble getting email?</h1>
                <br />
                <div>
                  <p>Try these common fixes:</p>
                  <ul className="ml-5 list-outside list-disc">
                    <li>Check if the email was marked as spam or filtered.</li>
                    <li>Check your internet connection.</li>
                    <li>Check that you did not misspell your email.</li>
                    <li>
                      Check that your inbox space is not running out or other
                      inbox settings related issues.
                    </li>
                  </ul>
                  <br />
                  <p>
                    If the steps above didn&apos;t work, you can resend the
                    email. Note that this will deactivate the link in the older
                    email.
                  </p>
                </div>
                <div className="mt-4 flex w-full items-center justify-between">
                  <button
                    type="button"
                    onClick={handleResendEmailLink}
                    disabled={loading}
                    className="font-inherit text-link cursor-pointer border-none bg-transparent p-0 disabled:cursor-default disabled:opacity-50"
                  >
                    Resend
                  </button>
                  <button
                    type="button"
                    className="border-borderDefault bg-surface-default hover:bg-surface-hover-subtle cursor-pointer rounded border px-4 py-2 text-sm"
                    onClick={() => setEmailLinkState("sent")}
                  >
                    Back
                  </button>
                </div>
              </>
            ) : (
              <>
                <h1>Sign-in email sent</h1>
                <img
                  className="my-2 h-10 w-10"
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/success_status.png"
                />
                <p>
                  A sign-in email with additional instructions was sent to{" "}
                  <b>{email}</b>. Check your email to complete sign-in.
                </p>
                <div className="mt-4 flex w-full items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setEmailLinkState("troubleshooting")}
                    className="font-inherit text-link cursor-pointer border-none bg-transparent p-0"
                  >
                    Trouble getting email?
                  </button>
                  <button
                    type="button"
                    className="border-borderDefault bg-surface-default hover:bg-surface-hover-subtle cursor-pointer rounded border px-4 py-2 text-sm"
                    onClick={() => {
                      setEmailLinkState("input");
                      setEmail("");
                    }}
                  >
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="custom-auth-ui flex flex-col">
        {signInOptions.map((option) => renderProviderButton(option))}
        {error && <div className="text-textError mt-2 text-sm">{error}</div>}
      </div>
    </>
  );
};
